import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHelp,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  FileText,
  GitBranch,
  LoaderCircle,
  MessageSquare,
  Monitor,
  Paperclip,
  Plus,
  ShieldCheck,
  SquareCheck,
  Terminal,
  Trash2,
} from "lucide-react";
import type {
  TaskDetail,
  Attachment,
  Lane,
  Run,
  ContextItem,
  Project,
} from "../../../server/src/models";
import { AttachmentPreview } from "./AttachmentPreview";
import { api, ago, bytes } from "../api";
import { type Notify, laneColor, kinds, IconButton, Badge } from "./ui";

export function TaskView({
  taskId,
  fullPage,
  onClose,
  onOpen,
  onChange,
  revision,
  notify,
  projects,
}: {
  taskId: string;
  fullPage: boolean;
  onClose: () => void;
  onOpen: (id: string, page?: boolean) => void;
  onChange: () => void;
  revision: number;
  notify: Notify;
  projects: Project[];
}) {
  const [data, setData] = useState<TaskDetail | null>(null),
    [lanes, setLanes] = useState<Lane[]>([]),
    [error, setError] = useState(""),
    [tab, setTab] = useState("all"),
    [activityFilter, setActivityFilter] = useState("all");
  const [editing, setEditing] = useState(false),
    [draft, setDraft] = useState({ title: "", description: "", criteria: "" }),
    [comment, setComment] = useState(""),
    [posting, setPosting] = useState(false),
    [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [showContext, setShowContext] = useState(false),
    [sendBack, setSendBack] = useState(false),
    [sendBackComment, setSendBackComment] = useState(""),
    [deleteConfirm, setDeleteConfirm] = useState(false),
    [preview, setPreview] = useState<Attachment | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let cancelled = false;
    api<TaskDetail>(`/v1/tasks/${taskId}`)
      .then(async (d) => {
        const l = await api<Lane[]>(`/v1/projects/${d.task.project}/lanes`);
        if (!cancelled) {
          setData(d);
          setLanes(l);
          setError("");
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, revision]);
  const refresh = () => {
    onChange();
  };
  async function mutate(
    path: string,
    method: string,
    body?: unknown,
    message?: string,
  ) {
    try {
      await api(path, method, body);
      refresh();
      if (message) notify(message);
      return true;
    } catch (e) {
      notify((e as Error).message, true);
      return false;
    }
  }
  const patch = (body: unknown) => mutate(`/v1/tasks/${taskId}`, "PATCH", body);
  async function move(lane: string, comment?: string) {
    if (
      await mutate(
        `/v1/tasks/${taskId}/move`,
        "POST",
        { lane, comment },
        `Moved to ${lanes.find((l) => l.key === lane)?.title ?? lane}.`,
      )
    ) {
      setSendBack(false);
      setSendBackComment("");
    }
  }
  async function upload(files: FileList | File[], linked = false) {
    setPosting(true);
    try {
      for (const file of Array.from(files)) {
        const f = new FormData();
        f.append("file", file);
        const a = await api<Attachment>(
          `/v1/tasks/${taskId}/attachments`,
          "POST",
          f,
        );
        if (linked) setPendingAttachments((p) => [...p, a]);
      }
      refresh();
      notify("Files attached.");
    } catch (e) {
      notify((e as Error).message, true);
    } finally {
      setPosting(false);
    }
  }
  async function postComment(e: FormEvent) {
    e.preventDefault();
    setPosting(true);
    if (
      await mutate(
        `/v1/tasks/${taskId}/comments`,
        "POST",
        { body: comment, attachment_ids: pendingAttachments.map((a) => a.id) },
        "Comment added.",
      )
    ) {
      setComment("");
      setPendingAttachments([]);
    }
    setPosting(false);
  }
  if (error)
    return (
      <div className="form-pad">
        <div className="form-error">{error}</div>
        <button className="button" onClick={onClose}>
          Back to board
        </button>
      </div>
    );
  if (!data)
    return (
      <div className="board-loading">
        <LoaderCircle className="spin" />
        Opening task…
      </div>
    );
  const task = data.task,
    project = projects.find((p) => p.key === task.project),
    current = task.current_run,
    questions = data.questions.filter((q) => !q.answered_at),
    template = project?.settings.lane_templates[task.lane] ?? "continue {id}",
    instruction = template
      .replaceAll("{id}", task.id)
      .replaceAll("{base}", project?.base_branch ?? "main");
  const totalUsd = data.runs.reduce((sum, r) => sum + (r.usage?.usd ?? 0), 0),
    hasUsage = data.runs.some((r) => r.usage),
    context = data.context.filter((c) => tab === "all" || c.kind === tab);
  const activity = data.activity.items.filter(
    (e) =>
      activityFilter === "all" ||
      (activityFilter === "comments"
        ? e.type.startsWith("comment.")
        : e.type.startsWith("run.")),
  );
  const copy = (text: string) => {
    void navigator.clipboard
      .writeText(text)
      .then(() => notify("Copied."))
      .catch(() =>
        notify("Clipboard unavailable. Select and copy the text.", true),
      );
  };
  return (
    <div className={`task-view ${fullPage ? "full-page" : ""}`}>
      {fullPage && (
        <header className="topbar">
          <button className="text-button back-button" onClick={onClose}>
            <ArrowLeft size={15} />
            Board
          </button>
          <div className="breadcrumb">
            {project?.name ?? task.project}
            <ChevronRight size={13} />
            <span className="mono">{task.id}</span>
          </div>
          <button
            className="button small"
            onClick={() =>
              copy(
                `${task.id}: ${task.title}\n\n${task.handoff?.handoff?.done ?? ""}\nNext: ${task.handoff?.handoff?.next ?? instruction}`,
              )
            }
          >
            <Copy size={13} />
            Copy handoff
          </button>
        </header>
      )}
      <div className="task-title-block">
        <div className="task-title-meta">
          <span className="mono task-id">{task.id}</span>
          <Badge tone={laneColor(task.lane)}>
            <span className={`dot ${laneColor(task.lane)}`} />
            {lanes.find((l) => l.key === task.lane)?.title}
          </Badge>
          {task.archived_at && <Badge>Archived</Badge>}
          <div className="push-right">
            {!fullPage && (
              <button
                className="text-button"
                onClick={() => onOpen(taskId, true)}
              >
                Open as page
                <ExternalLink size={13} />
              </button>
            )}
            <button
              className="text-button"
              onClick={() => {
                setDraft({
                  title: task.title,
                  description: task.description,
                  criteria: task.criteria.map((c) => c.text).join("\n"),
                });
                setEditing(!editing);
              }}
            >
              {editing ? "Cancel" : "Edit task"}
            </button>
          </div>
        </div>
        {editing ? (
          <form
            className="edit-task-form"
            onSubmit={(e) => {
              e.preventDefault();
              void patch({
                title: draft.title,
                description: draft.description,
                criteria: draft.criteria
                  .split("\n")
                  .filter((s) => s.trim())
                  .map((text) => ({
                    text,
                    met:
                      task.criteria.find((c) => c.text === text)?.met ?? false,
                  })),
              }).then((ok) => {
                if (ok) setEditing(false);
              });
            }}
          >
            <label>
              Title
              <input
                value={draft.title}
                required
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>
            <label>
              Description
              <textarea
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
              />
            </label>
            <label>
              Acceptance criteria
              <textarea
                value={draft.criteria}
                onChange={(e) =>
                  setDraft({ ...draft, criteria: e.target.value })
                }
              />
            </label>
            <button className="button primary small">Save changes</button>
          </form>
        ) : (
          <h1>{task.title}</h1>
        )}
        <div className="task-tags">
          {task.tags.map((t) => (
            <Badge key={t}>#{t}</Badge>
          ))}
        </div>
      </div>
      <div className="task-layout">
        <div className="task-main">
          <section className="handoff-box">
            <div className="section-heading">
              <h2>
                <ArrowRight size={16} />
                Where it stands
              </h2>
              {task.handoff && (
                <span className="subtle">{ago(task.handoff.ended_at)} ago</span>
              )}
            </div>
            {task.handoff?.handoff ? (
              <>
                <div className="handoff-row">
                  <span>DONE SO FAR</span>
                  <p>{task.handoff.handoff.done}</p>
                </div>
                <div className="handoff-row">
                  <span>NEXT STEP</span>
                  <p>{task.handoff.handoff.next}</p>
                </div>
                {task.handoff.handoff.blocked && (
                  <div className="handoff-row blocked">
                    <span>BLOCKED ON</span>
                    <p>{task.handoff.handoff.blocked}</p>
                  </div>
                )}
                <footer>
                  <Terminal size={12} />
                  <span className="mono">{task.handoff.session_name}</span>
                </footer>
              </>
            ) : (
              <div className="handoff-placeholder">
                <p>No handoff yet.</p>
                <span>
                  The next agent will leave what it did, what comes next, and
                  anything that got in the way.
                </span>
              </div>
            )}
          </section>
          {questions.length > 0 && (
            <section className="questions-box">
              <div className="section-heading">
                <h2>
                  <CircleHelp size={17} />A little input from you
                </h2>
                <Badge tone="purple">{questions.length} open</Badge>
              </div>
              <p className="section-description">
                Your answers become decisions in the task’s memory.
              </p>
              {questions.map((q) => (
                <QuestionForm
                  key={q.id}
                  question={q}
                  onAnswer={async (text, suggestion_idx) => {
                    await mutate(
                      `/v1/tasks/${taskId}/answers`,
                      "POST",
                      {
                        answers: [{ question_id: q.id, text, suggestion_idx }],
                      },
                      "Answer saved as a decision.",
                    );
                  }}
                />
              ))}
            </section>
          )}
          {task.lane === "wait_user" && (
            <section className="review-box">
              <div>
                <h3>
                  <ShieldCheck size={17} />
                  Ready for your review
                </h3>
                <p>
                  The agent has handed this back to you. Approve it or leave a
                  next step.
                </p>
              </div>
              <div className="review-actions">
                <button className="button" onClick={() => setSendBack(true)}>
                  <ArrowLeft size={14} />
                  Send back
                </button>
                <button
                  className="button primary"
                  disabled={!!current}
                  onClick={() => void move("done")}
                >
                  <Check size={15} />
                  Approve & mark Done
                </button>
              </div>
            </section>
          )}
          {sendBack && (
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                void move(
                  task.lane === "ready" ? "backlog" : "ready",
                  sendBackComment,
                );
              }}
            >
              <label>
                What should the next run address?
                <textarea
                  autoFocus
                  required
                  rows={3}
                  value={sendBackComment}
                  onChange={(e) => setSendBackComment(e.target.value)}
                  placeholder="Describe what needs another look…"
                />
              </label>
              <div className="form-actions">
                <button
                  type="button"
                  className="button small"
                  onClick={() => setSendBack(false)}
                >
                  Cancel
                </button>
                <button className="button primary small">
                  Send back with comment
                </button>
              </div>
            </form>
          )}
          {(current ?? data.runs.find((r) => r.checklist.length)) && (
            <section className="task-section">
              <div className="section-heading">
                <h2>
                  <SquareCheck size={16} />
                  Run checklist
                </h2>
                <span className="subtle">
                  {
                    (current ??
                      data.runs.find(
                        (r) => r.checklist.length,
                      ))!.checklist.filter((c) => c.state === "done").length
                  }{" "}
                  completed
                </span>
              </div>
              <div className="checklist">
                {(current ??
                  data.runs.find((r) => r.checklist.length))!.checklist.map(
                  (c, i) => (
                    <div key={i} className={c.state}>
                      {c.state === "done" ? (
                        <Check size={15} />
                      ) : c.state === "current" ? (
                        <span className="dot amber" />
                      ) : (
                        <Circle size={14} />
                      )}
                      <span>{c.text}</span>
                      {c.state === "current" && (
                        <Badge tone="amber">
                          {current ? "In progress" : "Stopped here"}
                        </Badge>
                      )}
                    </div>
                  ),
                )}
              </div>
            </section>
          )}
          <section className="task-section">
            <div className="section-heading">
              <h2>
                <FileText size={16} />
                Description
              </h2>
            </div>
            <p className={`preserve ${!task.description ? "subtle" : ""}`}>
              {task.description ||
                "No description yet. Add the problem and expected outcome."}
            </p>
            {task.criteria.length > 0 && (
              <>
                <div className="criteria-heading">
                  Acceptance criteria
                  <span>
                    {task.criteria.filter((c) => c.met).length} /{" "}
                    {task.criteria.length}
                  </span>
                </div>
                {task.criteria.map((criterion, i) => (
                  <label className="criterion" key={i}>
                    <input
                      type="checkbox"
                      checked={criterion.met}
                      onChange={() =>
                        void patch({
                          criteria: task.criteria.map((c, n) => ({
                            ...c,
                            met: n === i ? !c.met : c.met,
                          })),
                        })
                      }
                    />
                    <span className={criterion.met ? "checked" : ""}>
                      {criterion.text}
                    </span>
                  </label>
                ))}
              </>
            )}
          </section>
          <section className="task-section">
            <div className="section-heading">
              <h2>
                <Paperclip size={16} />
                Attachments
                <span className="count">{data.attachments.length}</span>
              </h2>
              <button
                className="text-button"
                disabled={posting}
                onClick={() => fileInput.current?.click()}
              >
                <Plus size={13} />
                Attach file
              </button>
              <input
                type="file"
                multiple
                ref={fileInput}
                className="visually-hidden"
                aria-label="Upload attachments"
                onChange={(e) => {
                  if (e.target.files) void upload(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            <div
              className="attachments-grid"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files.length)
                  void upload(e.dataTransfer.files);
              }}
            >
              {!data.attachments.length ? (
                <button
                  className="attachment-drop"
                  onClick={() => fileInput.current?.click()}
                >
                  <Paperclip size={18} />
                  <span>
                    Drop a screenshot or log here
                    <small>Keep the useful evidence with the work.</small>
                  </span>
                  <Plus size={15} />
                </button>
              ) : (
                data.attachments.map((a) => (
                  <div className="attachment" key={a.id}>
                    <button onClick={() => setPreview(a)}>
                      {[
                        "image/png",
                        "image/jpeg",
                        "image/gif",
                        "image/webp",
                      ].includes(a.mime) ? (
                        <img
                          src={`/v1/attachments/${a.id}?inline=1`}
                          alt={a.caption || a.filename}
                        />
                      ) : (
                        <FileText size={22} />
                      )}
                      <span>
                        <b>{a.filename}</b>
                        <small>
                          {bytes(a.size)} · {a.author}
                        </small>
                      </span>
                    </button>
                    <a
                      className="icon-button"
                      aria-label={`Download ${a.filename}`}
                      href={`/v1/attachments/${a.id}`}
                    >
                      <Download size={14} />
                    </a>
                    <IconButton
                      label={`Delete ${a.filename}`}
                      onClick={() =>
                        void mutate(
                          `/v1/attachments/${a.id}`,
                          "DELETE",
                          undefined,
                          "Attachment removed.",
                        )
                      }
                    >
                      <Trash2 size={13} />
                    </IconButton>
                  </div>
                ))
              )}
            </div>
          </section>
          <section className="task-section activity-section">
            <div className="section-heading">
              <h2>
                <Clock3 size={16} />
                Activity
              </h2>
              <div className="mini-tabs">
                {["all", "comments", "runs"].map((f) => (
                  <button
                    key={f}
                    className={activityFilter === f ? "active" : ""}
                    onClick={() => setActivityFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <form
              className="comment-composer"
              onSubmit={postComment}
              onPaste={(e) => {
                if (e.clipboardData.files.length) {
                  e.preventDefault();
                  void upload(e.clipboardData.files, true);
                }
              }}
            >
              <div className="composer-body">
                <span className="avatar tiny">A</span>
                <textarea
                  aria-label="Write a comment"
                  placeholder="Leave a note for the next session…"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
              {pendingAttachments.length > 0 && (
                <div className="pending-files">
                  {pendingAttachments.map((a) => (
                    <Badge key={a.id}>
                      <Paperclip size={11} />
                      {a.filename}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="composer-footer">
                <label className="text-button">
                  <Paperclip size={14} />
                  Attach
                  <input
                    type="file"
                    multiple
                    className="visually-hidden"
                    aria-label="Attach files to comment"
                    onChange={(e) => {
                      if (e.target.files) void upload(e.target.files, true);
                      e.target.value = "";
                    }}
                  />
                </label>
                <span className="subtle">Paste images here, too.</span>
                <button
                  className="button primary small"
                  disabled={posting || !comment.trim()}
                >
                  {posting ? <LoaderCircle size={13} className="spin" /> : null}
                  Comment
                </button>
              </div>
            </form>
            <div className="timeline">
              {activity.length ? (
                activity.map((event) => {
                  const c =
                    event.type === "comment.added"
                      ? data.comments.find((c) => c.id === event.payload.id)
                      : null;
                  return (
                    <div
                      className={`timeline-item ${c ? "is-comment" : ""}`}
                      key={event.id}
                    >
                      <span
                        className={`timeline-marker ${c ? "comment-marker" : ""}`}
                      >
                        {c ? <MessageSquare size={12} /> : <span />}
                      </span>
                      <div className="timeline-content">
                        <div className="timeline-heading">
                          <strong>
                            {event.actor === "Andika" ? "You" : event.actor}
                          </strong>
                          <span>{ago(event.created_at)} ago</span>
                          {c && (
                            <IconButton
                              label="Delete comment"
                              onClick={() =>
                                void mutate(`/v1/comments/${c.id}`, "DELETE")
                              }
                            >
                              <Trash2 size={12} />
                            </IconButton>
                          )}
                        </div>
                        {c ? (
                          <>
                            <p className="preserve">{c.body}</p>
                            {data.attachments
                              .filter((a) => a.comment_id === c.id)
                              .map((a) => (
                                <button
                                  className="attachment-link"
                                  key={a.id}
                                  onClick={() => setPreview(a)}
                                >
                                  <Paperclip size={12} />
                                  {a.filename}
                                </button>
                              ))}
                          </>
                        ) : (
                          <p>{eventText(event.type, event.payload)}</p>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="subtle">No activity in this filter.</p>
              )}
            </div>
            {data.activity.more && (
              <button
                className="button small load-more"
                onClick={() => {
                  void api<TaskDetail["activity"]>(data.activity.more!)
                    .then((page) =>
                      setData({
                        ...data,
                        activity: {
                          ...page,
                          items: [...data.activity.items, ...page.items],
                        },
                      }),
                    )
                    .catch((e) => notify(e.message, true));
                }}
              >
                Load earlier activity
              </button>
            )}
          </section>
        </div>
        <aside className="task-rail">
          <section className="next-run">
            <div className="section-heading">
              <h3>
                <Terminal size={15} />
                Start the next run
              </h3>
              <IconButton
                label="Copy next instruction"
                onClick={() => copy(instruction)}
              >
                <Copy size={14} />
              </IconButton>
            </div>
            <code>{instruction || `review ${task.id}`}</code>
            <p>One line for your agent. The briefing brings the rest.</p>
          </section>
          <section className="rail-properties">
            <label>
              Status
              <select
                aria-label="Task status"
                value={task.lane}
                onChange={(e) => {
                  if (task.lane === "ready" && e.target.value === "backlog")
                    setSendBack(true);
                  else void move(e.target.value);
                }}
              >
                {lanes.map((l) => (
                  <option key={l.key} value={l.key}>
                    {l.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Priority
              <select
                aria-label="Task priority"
                value={task.priority}
                onChange={(e) => void patch({ priority: e.target.value })}
              >
                {["urgent", "high", "normal", "low"].map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
            <label>
              Tags
              <input
                aria-label="Task tags"
                key={task.tags.join(",")}
                defaultValue={task.tags.join(", ")}
                placeholder="Add tags, separated by commas"
                onBlur={(e) => {
                  const tags = e.target.value
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean);
                  if (tags.join(",") !== task.tags.join(","))
                    void patch({ tags });
                }}
              />
            </label>
            <label>
              Branch
              <input
                aria-label="Task branch"
                key={task.branch}
                defaultValue={task.branch}
                placeholder="Optional branch name"
                onBlur={(e) => {
                  if (e.target.value !== task.branch)
                    void patch({ branch: e.target.value });
                }}
              />
            </label>
            <span className="property-footnote">
              <Clock3 size={12} />
              {ago(task.lane_entered_at)} in this lane
              {!lanes.find((l) => l.key === task.lane)?.clock
                ? " · clock paused"
                : ""}
            </span>
          </section>
          <section className="rail-section">
            <h3>
              <Monitor size={15} />
              Current run
            </h3>
            {current ? (
              <>
                <div className="current-run">
                  <span
                    className={`dot ${current.stale ? "amber" : "green"}`}
                  />
                  <span>
                    <b>{current.machine}</b>
                    <code>{current.session_name}</code>
                  </span>
                </div>
                <p className="subtle preserve">{current.instruction}</p>
                {current.stale && (
                  <Badge tone="amber">
                    Stale · {ago(current.last_activity_at)} idle
                  </Badge>
                )}
                <button
                  className="text-button danger"
                  onClick={() =>
                    void mutate(
                      `/v1/runs/${current.id}/abandon`,
                      "POST",
                      undefined,
                      "Run ended and claim released.",
                    )
                  }
                >
                  Mark run ended
                </button>
              </>
            ) : (
              <p className="subtle">
                No session running.
                <br />
                The next instruction is yours.
              </p>
            )}
          </section>
          <section className="rail-section context-section">
            <div className="section-heading">
              <h3>
                <BookOpen size={15} />
                Context<span className="count">{data.context.length}</span>
              </h3>
              <IconButton
                label="Add context"
                onClick={() => setShowContext(!showContext)}
              >
                <Plus size={15} />
              </IconButton>
            </div>
            <p className="subtle">Memory for every run that follows.</p>
            <div className="context-tabs">
              {["all", ...kinds].map((k) => (
                <button
                  key={k}
                  className={tab === k ? "active" : ""}
                  onClick={() => setTab(k)}
                >
                  {k}
                </button>
              ))}
            </div>
            {showContext && (
              <ContextForm
                onCancel={() => setShowContext(false)}
                onSave={async (input) => {
                  if (
                    await mutate(
                      `/v1/tasks/${taskId}/context`,
                      "POST",
                      input,
                      "Context saved.",
                    )
                  )
                    setShowContext(false);
                }}
              />
            )}
            {context.length ? (
              context.map((c) => (
                <ContextCard
                  key={c.id}
                  item={c}
                  attachments={data.attachments.filter(
                    (a) => a.context_id === c.id,
                  )}
                  onPreview={setPreview}
                  onDelete={() => void mutate(`/v1/context/${c.id}`, "DELETE")}
                  onAttach={async (file) => {
                    const f = new FormData();
                    f.append("file", file);
                    f.append("context_id", c.id);
                    await mutate(
                      `/v1/tasks/${taskId}/attachments`,
                      "POST",
                      f,
                      "File attached to context.",
                    );
                  }}
                />
              ))
            ) : (
              <div className="context-empty">
                Save a decision, gotcha, or file reference. The next session
                will thank you.
              </div>
            )}
          </section>
          {(data.related.length > 0 || task.parent) && (
            <section className="rail-section">
              <h3>
                <GitBranch size={15} />
                Task tree & relations
              </h3>
              {data.related.map((t) => (
                <button
                  className="related-task"
                  key={t.id}
                  onClick={() => onOpen(t.id, fullPage)}
                >
                  <span className={`dot ${laneColor(t.lane)}`} />
                  <span>
                    <small className="mono">
                      {t.id}{" "}
                      {t.id === task.parent
                        ? "· parent"
                        : t.parent === task.id
                          ? "· child"
                          : ""}
                    </small>
                    {t.title}
                  </span>
                  <ChevronRight size={13} />
                </button>
              ))}
            </section>
          )}
          <section className="rail-section">
            <div className="section-heading">
              <h3>
                <Terminal size={15} />
                Runs<span className="count">{data.runs.length}</span>
              </h3>
              {hasUsage && project?.settings.show_spend !== false && (
                <Badge>${totalUsd.toFixed(2)}</Badge>
              )}
            </div>
            {project?.settings.show_spend &&
              project.settings.warn_usd > 0 &&
              totalUsd > project.settings.warn_usd && (
                <p className="form-error">
                  Reported spend is over the $
                  {project.settings.warn_usd.toFixed(2)} task warning.
                </p>
              )}
            {project?.settings.show_spend &&
              project.settings.warn_tokens > 0 &&
              data.runs.some(
                (run) =>
                  (run.usage?.tokens_in ?? 0) + (run.usage?.tokens_out ?? 0) >
                  project.settings.warn_tokens,
              ) && (
                <p className="form-error">
                  A run exceeded the configured token warning.
                </p>
              )}
            {data.runs.length ? (
              data.runs.map((r) => (
                <RunCard
                  key={r.id}
                  run={r}
                  showSpend={project?.settings.show_spend !== false}
                />
              ))
            ) : (
              <p className="subtle">A fresh start. No runs yet.</p>
            )}
          </section>
          <div className="task-danger-zone">
            {deleteConfirm ? (
              <div>
                <p>Delete this task? Its audit history is retained.</p>
                <button
                  className="button danger small"
                  onClick={() => {
                    void mutate(
                      `/v1/tasks/${taskId}`,
                      "DELETE",
                      undefined,
                      "Task deleted.",
                    ).then((ok) => {
                      if (ok) onClose();
                    });
                  }}
                >
                  Delete task
                </button>
                <button
                  className="text-button"
                  onClick={() => setDeleteConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="text-button"
                onClick={() => setDeleteConfirm(true)}
              >
                <Trash2 size={12} />
                Delete task
              </button>
            )}
          </div>
        </aside>
      </div>
      {preview && (
        <AttachmentPreview
          key={preview.id}
          attachment={preview}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
function QuestionForm({
  question,
  onAnswer,
}: {
  question: TaskDetail["questions"][number];
  onAnswer: (text: string, index?: number) => Promise<void>;
}) {
  const [text, setText] = useState(""),
    [choice, setChoice] = useState<number | undefined>(),
    [busy, setBusy] = useState(false);
  return (
    <form
      className="question"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        void onAnswer(text, choice).finally(() => setBusy(false));
      }}
    >
      <h3>{question.text}</h3>
      <div className="suggestions">
        {question.suggestions.map((s, i) => (
          <button
            type="button"
            key={i}
            className={`suggestion ${choice === i ? "chosen" : ""}`}
            onClick={() => {
              setChoice(i);
              setText(s.label);
            }}
          >
            <span className="radio-dot">{choice === i && <span />}</span>
            <span>
              <b>
                {s.label}
                {s.recommended && (
                  <span className="recommended">Agent’s pick</span>
                )}
              </b>
              {s.why && <small>{s.why}</small>}
            </span>
          </button>
        ))}
      </div>
      <label className="answer-input">
        Your answer
        <textarea
          aria-label={`Answer: ${question.text}`}
          rows={2}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setChoice(undefined);
          }}
          placeholder="Choose a suggestion or write your own answer."
          required
        />
      </label>
      <button className="button primary small" disabled={busy || !text.trim()}>
        <Check size={13} />
        Save answer
      </button>
    </form>
  );
}
function ContextForm({
  onSave,
  onCancel,
}: {
  onSave: (input: unknown) => Promise<void>;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="context-form"
      onSubmit={(e) => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        setBusy(true);
        void onSave(Object.fromEntries(f)).finally(() => setBusy(false));
      }}
    >
      <label>
        Kind
        <select name="kind">
          {kinds.map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
      </label>
      <label>
        Title
        <input name="title" placeholder="A short reference" />
      </label>
      <label>
        Context
        <textarea
          name="body"
          rows={4}
          required
          placeholder="What should the next run know?"
        />
      </label>
      <label>
        Why it matters
        <input name="why" placeholder="Optional" />
      </label>
      <div className="form-actions">
        <button type="button" className="text-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="button primary small" disabled={busy}>
          Save context
        </button>
      </div>
    </form>
  );
}
function ContextCard({
  item,
  attachments,
  onDelete,
  onAttach,
  onPreview,
}: {
  item: ContextItem;
  attachments: Attachment[];
  onPreview: (attachment: Attachment) => void;
  onDelete: () => void;
  onAttach: (file: File) => Promise<void>;
}) {
  return (
    <div className={`context-card ${item.kind}`}>
      <div>
        <Badge
          tone={
            item.kind === "decision"
              ? "green"
              : item.kind === "gotcha"
                ? "amber"
                : item.kind === "failed"
                  ? "red"
                  : "gray"
          }
        >
          {item.kind}
        </Badge>
        <IconButton
          label={`Delete context ${item.title || item.kind}`}
          onClick={onDelete}
        >
          <Trash2 size={11} />
        </IconButton>
      </div>
      {item.title && <h4>{item.title}</h4>}
      <p className="preserve">{item.body}</p>
      {item.why && <p className="context-why">{item.why}</p>}
      {attachments.map((a) => (
        <button
          className="attachment-link"
          key={a.id}
          onClick={() => onPreview(a)}
        >
          <Paperclip size={12} />
          {a.filename}
        </button>
      ))}
      <footer>
        <span>
          {item.author === "Andika" ? "You" : item.author} ·{" "}
          {ago(item.created_at)}
        </span>
        <label className="text-button" title="Attach to context">
          <Paperclip size={11} />
          <input
            type="file"
            className="visually-hidden"
            aria-label={`Attach file to ${item.title || item.kind}`}
            onChange={(e) => {
              if (e.target.files?.[0]) void onAttach(e.target.files[0]);
            }}
          />
        </label>
      </footer>
    </div>
  );
}
function RunCard({ run, showSpend }: { run: Run; showSpend: boolean }) {
  return (
    <details className="run-card">
      <summary>
        <span
          className={`dot ${run.ended_at ? "gray" : run.stale ? "amber" : "green"}`}
        />
        <span>
          <b>{run.session_name.split(":").slice(1).join(":")}</b>
          <small>
            {run.outcome?.replace("_", " ") ?? "Running"} ·{" "}
            {ago(run.started_at)} ago
            {showSpend && run.usage?.usd !== undefined
              ? ` · $${run.usage.usd.toFixed(2)}`
              : ""}
          </small>
        </span>
        <ChevronDown size={12} />
      </summary>
      <p>{run.instruction}</p>
      {run.handoff && (
        <>
          <b>Done</b>
          <p>{run.handoff.done}</p>
          <b>Next</b>
          <p>{run.handoff.next}</p>
        </>
      )}
      {run.transcript_ref && (
        <p className="mono break-all">{run.transcript_ref}</p>
      )}
      <a
        className="text-button"
        href={`/v1/runs/${run.id}/briefing.md`}
        download={`${run.task_id}-briefing.md`}
      >
        <Download size={12} />
        Briefing
      </a>
    </details>
  );
}
function eventText(type: string, p: Record<string, unknown>) {
  if (type === "task.moved")
    return `Moved from ${String(p.from).replaceAll("_", " ")} to ${String(p.to).replaceAll("_", " ")}.`;
  if (type === "task.created") return "Created this task.";
  if (type === "task.updated")
    return `Updated ${((p.fields as string[]) ?? []).join(", ")}.`;
  if (type === "run.started")
    return `Started a ${p.mode === "edit" ? "build" : "read"} run: ${p.instruction}`;
  if (type === "run.ended")
    return `Left a handoff · ${String(p.outcome).replaceAll("_", " ")}.`;
  if (type === "run.resumed")
    return "Resumed after session revival with a fresh briefing.";
  if (type === "attachment.added") return `Attached ${p.filename}.`;
  if (type === "context.added") return `Saved a ${p.kind} in context.`;
  if (type === "question.asked") return `Asked: ${p.text}`;
  if (type === "question.answered")
    return "Answered a question and saved the decision.";
  if (type === "run.abandoned")
    return `Run ended · ${p.reason === "expired" ? "session expired" : "released by human"}.`;
  return type.replaceAll(".", " ").replaceAll("_", " ") + ".";
}
